import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { accountIssueFromToken, accountIssueRoute } from '../utils/account';

// Lazy load views
const Calendar = () => import('../views/Calendar.vue');
const Login = () => import('../views/Login.vue');
const CallbackView = () => import('../views/CallbackView.vue');
const AdminView = () => import('../views/AdminView.vue');
const Profile = () => import('../views/Profile.vue');
const Upcoming = () => import('../views/Upcoming.vue');
const ErrorPage = () => import('../views/ErrorPage.vue');
const GuestInviteView = () => import('../views/GuestInviteView.vue');
const DisabledAccountView = () => import('../views/DisabledAccountView.vue');
const InsufficientScopeView = () => import('../views/InsufficientScopeView.vue');
const DocsView = () => import('../views/DocsView.vue');

declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresGuest?: boolean;
        requiresAdmin?: boolean;
        /** Standalone page without the app header and tab bar */
        bare?: boolean;
    }
}

const routes: RouteRecordRaw[] = [
    { path: '/', name: 'login', component: Login, meta: { requiresGuest: true, bare: true } },
    { path: '/callback', name: 'callback', component: CallbackView, meta: { bare: true } },
    { path: '/disabled', name: 'disabled-account', component: DisabledAccountView, meta: { bare: true } },
    {
        path: '/insufficient-scope',
        name: 'insufficient-scope',
        component: InsufficientScopeView,
        meta: { bare: true }
    },
    {
        path: '/calendar',
        name: 'calendar',
        component: Calendar,
        meta: { requiresAuth: true }
    },
    {
        path: '/upcoming',
        name: 'upcoming',
        component: Upcoming,
        meta: { requiresAuth: true }
    },
    { path: '/event/:id', name: 'calendar-event', component: Calendar, meta: { requiresAuth: true } },
    { path: '/event/:id/chat', name: 'calendar-event-chat', component: Calendar, meta: { requiresAuth: true } },
    {
        path: '/event/:id/audit-log',
        name: 'calendar-event-audit-log',
        component: Calendar,
        meta: { requiresAuth: true }
    },
    { path: '/event/:id/map', name: 'calendar-event-map', component: Calendar, meta: { requiresAuth: true } },
    { path: '/event/:id/edit', name: 'calendar-event-edit', component: Calendar, meta: { requiresAuth: true } },
    { path: '/upcoming/:id', name: 'upcoming-event', component: Upcoming, meta: { requiresAuth: true } },
    { path: '/upcoming/:id/chat', name: 'upcoming-event-chat', component: Upcoming, meta: { requiresAuth: true } },
    {
        path: '/upcoming/:id/audit-log',
        name: 'upcoming-event-audit-log',
        component: Upcoming,
        meta: { requiresAuth: true }
    },
    { path: '/upcoming/:id/edit', name: 'upcoming-event-edit', component: Upcoming, meta: { requiresAuth: true } },
    { path: '/settings', name: 'settings', component: AdminView, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin', redirect: { name: 'settings' } },
    { path: '/invite/:token', name: 'guest-invite', component: GuestInviteView, meta: { bare: true } },
    { path: '/profile', name: 'profile', component: Profile, meta: { requiresAuth: true } },
    { path: '/docs', name: 'docs', component: DocsView, meta: { bare: true } },
    { path: '/403', name: 'forbidden', component: ErrorPage, props: { status: 403 } },
    { path: '/404', name: 'not-found', component: ErrorPage, props: { status: 404 } },
    { path: '/:pathMatch(.*)*', component: ErrorPage, props: { status: 404 } }
];

export const router = createRouter({
    history: createWebHistory(),
    routes
}) as any;

router.previousRoute = null;

// Navigation guards
router.beforeEach(async (to: any, from: any) => {
    router.previousRoute = from;

    // Back from Legacy Auth: the callback view completes the sign-in itself.
    if (to.name === 'callback') return;

    // Resumes the stored session (refreshing its access token) once per page load.
    const { useSessionStore } = await import('../stores/session');
    const sessionStore = useSessionStore();
    await sessionStore.restore();

    const isAuthenticated = sessionStore.isAuthenticated;
    const accountIssue = accountIssueFromToken(sessionStore.accessToken);

    // Send the user to sign in if the route requires auth and nobody is signed in, and bring them
    // back to the page they asked for afterwards.
    if (to.meta.requiresAuth && !isAuthenticated) {
        return { name: 'login', query: to.fullPath === '/calendar' ? {} : { next: to.fullPath } };
    }

    // Disabled accounts and tokens without the calendar scope go to their notice page,
    // whether they are entering the app or trying to visit the login page again.
    if ((to.meta.requiresAuth || to.meta.requiresGuest) && accountIssue) {
        return { name: accountIssueRoute(accountIssue) };
    }

    // The event view needs the profile to know whether this user can join, so load it before
    // rendering a protected page. This is also what completes the sign-in after the OIDC callback,
    // where the app shell loaded before the tokens existed.
    if (to.meta.requiresAuth && !sessionStore.currentUser) {
        await sessionStore.load();
    }

    // Redirect to calendar if user is already authenticated and tries to access login
    if (to.meta.requiresGuest && isAuthenticated) {
        return { name: 'calendar' };
    }

    // Redirect to calendar if route requires admin and user is not an admin
    if (to.meta.requiresAdmin) {
        // Ensure the profile is loaded before checking admin status
        if (!sessionStore.currentUser) {
            await sessionStore.load();
        }

        if (!sessionStore.currentUser?.isAdmin) {
            return { name: 'calendar' };
        }
    }

    return;
});
